import { Task, TaskCategory, TaskPriority, TaskStatus } from "@shared/types";
import {
  Camera,
  ChevronRight,
  ClipboardList,
  Leaf,
  Mic,
  Plus,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TasksAPI } from "../services/api";

interface TasksState {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  deleting: string | null;
  updating: string | null;
}

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<TasksState>({
    tasks: [],
    loading: true,
    error: null,
    deleting: null,
    updating: null,
  });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await TasksAPI.getAll();

      if (response.success && response.tasks) {
        setState((prev) => ({
          ...prev,
          tasks: response.tasks!,
          loading: false,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: response.error || "Failed to load tasks",
          loading: false,
        }));
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Failed to load tasks",
        loading: false,
      }));
    }
  };

  const updateTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    setState((prev) => ({ ...prev, updating: taskId }));

    try {
      const response = await TasksAPI.update(taskId, { status: newStatus });

      if (response.success && response.task) {
        setState((prev) => ({
          ...prev,
          tasks: prev.tasks.map((task) =>
            task.id === taskId ? { ...task, status: newStatus } : task
          ),
          updating: null,
        }));
      } else {
        setState((prev) => ({ ...prev, updating: null }));
        alert(`Failed to update task status: ${response.error}`);
      }
    } catch (error) {
      setState((prev) => ({ ...prev, updating: null }));
      alert("Failed to update task status");
    }
  };

  const getAvailableStatusActions = (currentStatus: TaskStatus) => {
    const actions: Array<{ status: TaskStatus; label: string; variant: string }> =
      [];

    switch (currentStatus) {
      case TaskStatus.DRAFT:
      case TaskStatus.SUBMITTED:
        actions.push({
          status: TaskStatus.IN_PROGRESS,
          label: "Start",
          variant: "harvest",
        });
        break;
      case TaskStatus.IN_PROGRESS:
        actions.push({
          status: TaskStatus.COMPLETED,
          label: "Complete",
          variant: "field",
        });
        break;
      case TaskStatus.CANCELLED:
        actions.push({
          status: TaskStatus.IN_PROGRESS,
          label: "Restart",
          variant: "harvest",
        });
        break;
      case TaskStatus.COMPLETED:
        break;
    }

    return actions;
  };

  const handleDeleteTask = async (taskId: string, taskTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${taskTitle}"?`)) {
      return;
    }

    setState((prev) => ({ ...prev, deleting: taskId }));

    try {
      const response = await TasksAPI.delete(taskId);

      if (response.success) {
        setState((prev) => ({
          ...prev,
          tasks: prev.tasks.filter((task) => task.id !== taskId),
          deleting: null,
        }));
      } else {
        setState((prev) => ({ ...prev, deleting: null }));
        alert(`Failed to delete task: ${response.error}`);
      }
    } catch (error) {
      setState((prev) => ({ ...prev, deleting: null }));
      alert("Failed to delete task");
    }
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
    });
  };

  const getStatusConfig = (status: TaskStatus) => {
    const configs = {
      [TaskStatus.DRAFT]: {
        dot: "bg-earth-400",
        text: "Draft",
        cardClass: "task-card-draft",
      },
      [TaskStatus.SUBMITTED]: {
        dot: "bg-blue-500",
        text: "Submitted",
        cardClass: "task-card-submitted",
      },
      [TaskStatus.IN_PROGRESS]: {
        dot: "bg-harvest",
        text: "In Progress",
        cardClass: "task-card-in-progress",
      },
      [TaskStatus.COMPLETED]: {
        dot: "bg-field",
        text: "Completed",
        cardClass: "task-card-completed",
      },
      [TaskStatus.CANCELLED]: {
        dot: "bg-alert-urgent",
        text: "Cancelled",
        cardClass: "task-card-draft",
      },
    };
    return configs[status] || configs[TaskStatus.DRAFT];
  };

  const getPriorityConfig = (priority: TaskPriority) => {
    const configs = {
      [TaskPriority.LOW]: { badge: "badge-earth", text: "Low" },
      [TaskPriority.MEDIUM]: { badge: "badge-info", text: "Medium" },
      [TaskPriority.HIGH]: { badge: "badge-harvest", text: "High" },
      [TaskPriority.URGENT]: { badge: "badge-alert", text: "Urgent" },
    };
    return configs[priority] || configs[TaskPriority.MEDIUM];
  };

  const getCategoryDisplay = (category: TaskCategory) => {
    if (!category) return "Other";
    return category
      .replace("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-earth-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-earth-200 border-t-field rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-earth-600 font-medium">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-earth-50 flex items-center justify-center px-4">
        <div className="text-center card p-8 max-w-sm">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-6 h-6 text-alert-urgent" />
          </div>
          <h2 className="font-display text-xl text-earth-900 mb-2">
            Unable to load tasks
          </h2>
          <p className="text-earth-600 mb-6">{state.error}</p>
          <button onClick={loadTasks} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const completedTasks = state.tasks.filter(
    (task) => task.status === TaskStatus.COMPLETED
  );
  const inProgressTasks = state.tasks.filter(
    (task) => task.status === TaskStatus.IN_PROGRESS
  );
  const pendingTasks = state.tasks.filter(
    (task) =>
      task.status === TaskStatus.SUBMITTED || task.status === TaskStatus.DRAFT
  );

  return (
    <div className="min-h-screen bg-earth-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-field rounded-full flex items-center justify-center shadow-soft">
                <Leaf className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="font-display text-2xl sm:text-3xl text-earth-900">
                  All Tasks
                </h1>
                <p className="text-sm text-earth-600">Waddington Park</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/submit")}
              className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              New Task
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 animate-slide-up"
          style={{ animationDelay: "50ms" }}
        >
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-earth-100 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-earth-700">
                  {state.tasks.length}
                </span>
              </div>
              <p className="text-sm font-medium text-earth-600 uppercase tracking-wide">
                Total
              </p>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-field-100 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-field-700">
                  {completedTasks.length}
                </span>
              </div>
              <p className="text-sm font-medium text-earth-600 uppercase tracking-wide">
                Done
              </p>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-harvest-100 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-harvest-700">
                  {inProgressTasks.length}
                </span>
              </div>
              <p className="text-sm font-medium text-earth-600 uppercase tracking-wide">
                Active
              </p>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-blue-700">
                  {pendingTasks.length}
                </span>
              </div>
              <p className="text-sm font-medium text-earth-600 uppercase tracking-wide">
                Pending
              </p>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div
          className="card animate-slide-up"
          style={{ animationDelay: "100ms" }}
        >
          <div className="card-header">
            <h2 className="font-display text-lg text-earth-900">Tasks List</h2>
            <p className="text-sm text-earth-600 mt-1">
              Tap any task to view details
            </p>
          </div>

          {state.tasks.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 bg-earth-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-earth-400" />
              </div>
              <h3 className="font-display text-lg text-earth-900 mb-2">
                No tasks yet
              </h3>
              <p className="text-earth-600 mb-6">
                Create your first task to get started
              </p>
              <button
                onClick={() => navigate("/submit")}
                className="btn-primary"
              >
                Create Task
              </button>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden divide-y divide-earth-200">
                {state.tasks.map((task, index) => {
                  const statusConfig = getStatusConfig(task.status);
                  const priorityConfig = getPriorityConfig(task.priority);

                  return (
                    <div
                      key={task.id}
                      onClick={() => handleTaskClick(task.id)}
                      className={`p-4 cursor-pointer transition-all duration-150 hover:bg-earth-50 active:bg-earth-100 border-l-4 ${
                        statusConfig.cardClass.includes("field")
                          ? "border-l-field"
                          : statusConfig.cardClass.includes("harvest")
                          ? "border-l-harvest"
                          : statusConfig.cardClass.includes("submitted")
                          ? "border-l-blue-500"
                          : "border-l-earth-300"
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Title */}
                      <h3 className="font-display text-lg text-earth-900 mb-3 pr-6">
                        {task.title}
                      </h3>

                      {/* Info Grid */}
                      <div className="flex justify-between">
                        <div className="flex-1 mr-4 space-y-2">
                          {/* Status */}
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full ${statusConfig.dot}`}
                            ></span>
                            <span className="text-sm text-earth-700">
                              {statusConfig.text}
                            </span>
                          </div>

                          {/* Priority & Category */}
                          <div className="flex flex-wrap gap-2">
                            <span className={priorityConfig.badge}>
                              {priorityConfig.text}
                            </span>
                            <span className="badge-earth">
                              {getCategoryDisplay(task.category)}
                            </span>
                          </div>

                          {/* Media indicators */}
                          {((task.photos?.length ?? 0) > 0 ||
                            (task.audioRecordings?.length ?? 0) > 0) && (
                            <div className="flex items-center gap-3 pt-1">
                              {task.photos && task.photos.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-earth-600">
                                  <Camera className="w-3.5 h-3.5" />
                                  {task.photos.length}
                                </span>
                              )}
                              {task.audioRecordings &&
                                task.audioRecordings.length > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs text-earth-600">
                                    <Mic className="w-3.5 h-3.5" />
                                    {task.audioRecordings.length}
                                  </span>
                                )}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2">
                          {getAvailableStatusActions(task.status).map(
                            (action) => (
                              <button
                                key={action.status}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateTaskStatus(task.id, action.status);
                                }}
                                disabled={state.updating === task.id}
                                className={`px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                                  state.updating === task.id
                                    ? "bg-earth-100 text-earth-400 cursor-not-allowed"
                                    : action.variant === "field"
                                    ? "bg-field text-white hover:bg-field-600"
                                    : "bg-harvest text-white hover:bg-harvest-600"
                                }`}
                              >
                                {state.updating === task.id
                                  ? "..."
                                  : action.label}
                              </button>
                            )
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTask(task.id, task.title);
                            }}
                            disabled={state.deleting === task.id}
                            className="px-3 py-2 rounded-md text-xs font-medium text-alert-urgent hover:bg-red-50 transition-all"
                          >
                            {state.deleting === task.id ? "..." : "Delete"}
                          </button>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-earth-100">
                        <span className="text-xs text-earth-500">
                          {formatDate(task.createdAt)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-earth-400" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-earth-50 border-b border-earth-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Media
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-earth-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-earth-100">
                    {state.tasks.map((task) => {
                      const statusConfig = getStatusConfig(task.status);
                      const priorityConfig = getPriorityConfig(task.priority);

                      return (
                        <tr
                          key={task.id}
                          onClick={() => handleTaskClick(task.id)}
                          className="hover:bg-earth-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="font-medium text-earth-900 truncate">
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-sm text-earth-500 truncate mt-0.5">
                                  {task.description}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-2 h-2 rounded-full ${statusConfig.dot}`}
                              ></span>
                              <span className="text-sm text-earth-700">
                                {statusConfig.text}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={priorityConfig.badge}>
                              {priorityConfig.text}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-earth-600">
                            {getCategoryDisplay(task.category)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {task.photos && task.photos.length > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-earth-100 text-earth-600">
                                  <Camera className="w-3 h-3" />
                                  {task.photos.length}
                                </span>
                              )}
                              {task.audioRecordings &&
                                task.audioRecordings.length > 0 && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-earth-100 text-earth-600">
                                    <Mic className="w-3 h-3" />
                                    {task.audioRecordings.length}
                                  </span>
                                )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-earth-600">
                            {formatDate(task.createdAt)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {getAvailableStatusActions(task.status).map(
                                (action) => (
                                  <button
                                    key={action.status}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateTaskStatus(task.id, action.status);
                                    }}
                                    disabled={state.updating === task.id}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                      state.updating === task.id
                                        ? "bg-earth-100 text-earth-400 cursor-not-allowed"
                                        : action.variant === "field"
                                        ? "bg-field text-white hover:bg-field-600"
                                        : "bg-harvest text-white hover:bg-harvest-600"
                                    }`}
                                  >
                                    {state.updating === task.id
                                      ? "..."
                                      : action.label}
                                  </button>
                                )
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTask(task.id, task.title);
                                }}
                                disabled={state.deleting === task.id}
                                className="p-1.5 rounded-md text-earth-400 hover:text-alert-urgent hover:bg-red-50 transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tasks;
